import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { updateInstitutionMember, uploadMemberPhoto } from "@/Services/institutions.service";
import { useAuth } from "@/context/authContext";
import { useQueryClient } from "@tanstack/react-query";
import { MemberForm, defaultMemberFormValues } from "./MemberForm";
import type { MemberFormValues } from "./MemberForm";
import type { InstitutionMember } from "../types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  member: InstitutionMember;
}

export function EditMemberDialog({ isOpen, onClose, member }: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [values, setValues] = useState<MemberFormValues>(defaultMemberFormValues());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setValues({
      first_name: member.first_name ?? "",
      last_name: member.last_name ?? "",
      second_last_name: member.second_last_name ?? "",
      dni: member.dni ?? "",
      birth_date: member.birth_date ?? "",
      gender: (member.gender as "M" | "F" | "") ?? "",
      email: member.email ?? "",
      cell_phone: member.cell_phone ?? "",
      district_id: member.district_id ?? "",
      region_id: member.region_id ?? "",
      photoFile: null,
    });
  }, [isOpen, member]);

  async function handleSave() {
    if (!user) return;
    setSaving(true);
    try {
      let photoFilename = member.profile_picture_filename;
      if (values.photoFile) {
        photoFilename = await uploadMemberPhoto(member.id, values.photoFile);
      }

      await updateInstitutionMember(
        member.id,
        {
          first_name: values.first_name.trim() || null,
          last_name: values.last_name.trim() || null,
          second_last_name: values.second_last_name.trim() || null,
          gender: values.gender || null,
          dni: values.dni.trim() || null,
          birth_date: values.birth_date || null,
          email: values.email.trim() || null,
          cell_phone: values.cell_phone.trim() || null,
          district_id: values.district_id || null,
          region_id: values.region_id || null,
          profile_picture_filename: photoFilename,
        },
        user.id,
      );

      // Invalidate all institution queries that may show this member
      await queryClient.invalidateQueries({ queryKey: ["institution-memberships"] });
      await queryClient.invalidateQueries({ queryKey: ["institution-authorities"] });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  const canSave = values.first_name.trim().length > 0 && values.last_name.trim().length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !saving && !o && onClose()}>
      <DialogContent className="flex max-h-[88vh] flex-col sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Editar miembro</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-1">
          <MemberForm
            values={values}
            onChange={setValues}
            disabled={saving}
            existingPhotoFilename={member.profile_picture_filename}
          />
        </div>

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving || !canSave}>
            {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
            Guardar cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
